import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { ukStamp } from "@/lib/time";
import { ChatAttachButton, ChatAttachment } from "@/components/portal/ChatAttach";
import { CheckCheck, Loader2, MessageCircle, Send } from "lucide-react";
import { ConnieMark } from "@/components/connie/ConnieMark";
import { ConnieText } from "@/components/connie/ConnieText";

/**
 * ChatPanel — the desk thread as a page, not a dock. Same endpoints and the same
 * testids as the floating widget, which is why the widget is not mounted on this route.
 */
export function ChatPanel({ height = "h-[min(70vh,620px)]" }) {
  const [messages, setMessages] = useState([]);
  const [desk, setDesk] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const scrollRef = useRef(null);
  const attachRef = useRef(null);

  const fetchThread = useCallback(async () => {
    try {
      const r = await api.get("/hub/chat/me?mark_read=1");
      setMessages(r.data.messages || []);
      if (r.data.desk) setDesk(r.data.desk);
      setLoaded(true);
    } catch (e) {
      if (e?.response?.status === 403) setDisabled(true);
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    let t;
    const tick = async () => { await fetchThread(); t = setTimeout(tick, 5000); };
    tick();
    return () => clearTimeout(t);
  }, [fetchThread]);

  const stick = useRef(true);
  useEffect(() => {
    const el = scrollRef.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const send = async () => {
    const body = input.trim();
    if (!body || sending) return;
    stick.current = true;
    setSending(true);
    try { await api.post("/hub/chat/me/messages", { body }); setInput(""); await fetchThread(); }
    catch { /* silent */ }
    finally { setSending(false); }
  };

  if (disabled) {
    return (
      <div className="grid place-items-center rounded-[14px] border border-[#E2E8F0] bg-white py-16 text-center text-[13px] text-slate-500" data-testid="portal-chat-disabled">
        Messaging belongs to a partner account. Sign in as the partner to use the thread.
      </div>
    );
  }

  const staffed = !!desk?.staffed;

  return (
    <div className="overflow-hidden rounded-[14px] border border-[#E2E8F0] bg-white shadow-[0_1px_2px_rgba(11,18,32,.04),0_10px_28px_-22px_rgba(11,18,32,.22)]" data-testid="portal-chat-panel">
      <header className="relative overflow-hidden bg-[#0B1220] px-5 py-4 text-white">
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(420px 200px at 100% -30%,rgba(0,85,255,.45),transparent 62%)" }} />
        <div className="relative flex items-center gap-2.5">
          <span className="relative flex size-2">
            {staffed && <span className="absolute inline-flex size-full animate-ping rounded-full bg-[#2ED39A] opacity-60" />}
            <span className={`relative inline-flex size-2 rounded-full ${staffed ? "bg-[#2ED39A]" : desk?.office_hours ? "bg-[#4d8bff]" : "bg-slate-500"}`} />
          </span>
          <div>
            <div className="font-archivo text-[15px] font-extrabold tracking-[-0.015em]">Connexis desk</div>
            <div className="mt-0.5 text-[11.5px] text-white/55" data-testid="portal-chat-desk-status">
              {desk ? `${desk.reply}${desk.hours ? ` · open ${desk.hours}` : ""}` : "Direct line to your Connexis team"}
            </div>
          </div>
        </div>
      </header>

      <div ref={scrollRef}
           onScroll={(e) => { const el = e.currentTarget; stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80; }}
           className={`${height} space-y-3 overflow-y-auto bg-[#F4F6F9] px-4 py-5`} data-testid="portal-chat-messages">
        {!loaded ? (
          <div className="grid h-full place-items-center text-slate-400"><Loader2 className="size-5 animate-spin" /></div>
        ) : messages.length === 0 ? (
          <div className="grid h-full place-items-center px-6 text-center">
            <div>
              <div className="mx-auto grid size-11 place-items-center rounded-[12px] border border-[#A9C2FF] bg-[#E4ECFF] text-[#0A3DB8]"><MessageCircle className="size-5" /></div>
              <div className="mt-3 font-archivo text-[14.5px] font-extrabold text-[#0B1220]">Start a conversation</div>
              <div className="mt-1.5 text-[12.5px] leading-relaxed text-slate-500">
                Ask about a case, your commission, documents — anything. The desk sees it instantly.
              </div>
            </div>
          </div>
        ) : messages.map((m) => <Bubble key={m.id} msg={m} />)}
      </div>

      <div className="flex items-end gap-2 border-t border-[#E2E8F0] bg-white p-3">
        <ChatAttachButton ref={attachRef} casesPath="/hub/chat/me/cases" uploadPath="/hub/chat/me/attachments"
                          onSent={fetchThread} testPrefix="portal-chat-attach" />
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          rows={1}
          placeholder="Write a message…"
          className="max-h-28 flex-1 resize-none rounded-[11px] border border-[#CBD5E1] bg-[#F8FAFF] px-3.5 py-2.5 text-[13px] text-slate-900 transition-colors placeholder:text-slate-400 focus:border-[#0055FF] focus:bg-white focus:outline-none"
          data-testid="portal-chat-input"
        />
        <button onClick={send} disabled={sending || !input.trim()}
                className="grid size-11 shrink-0 place-items-center rounded-[11px] bg-[#0055FF] text-white transition-colors hover:bg-[#0044CC] disabled:cursor-not-allowed disabled:opacity-40"
                data-testid="portal-chat-send">
          {sending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
        </button>
      </div>
    </div>
  );
}

const fmtTime = ukStamp;

function Bubble({ msg }) {
  if (msg.sender === "system") {
    return (
      <div className="flex justify-center" data-testid={`portal-chat-receipt-${msg.id}`}>
        <div className="max-w-[88%] rounded-[12px] border border-[#8BD6BF] bg-[#D8F2E9] px-4 py-2.5 text-center">
          <div className="flex items-center justify-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#085B43]">
            <CheckCheck className="size-3" /> Received
          </div>
          <div className="mt-1.5 text-[12.5px] leading-relaxed text-[#085B43]">{msg.body}</div>
          <div className="mt-1.5 text-[10.5px] text-[#085B43]/60">Automatic receipt · {fmtTime(msg.created_at)}</div>
        </div>
      </div>
    );
  }
  if (msg.sender === "connie") return <ConnieReply msg={msg} />;
  const mine = msg.sender === "party";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`portal-chat-msg-${msg.id}`}>
      <div className={`flex max-w-[80%] items-end gap-2 ${mine ? "flex-row-reverse" : ""}`}>
        {!mine && <div className="mb-0.5 grid size-6 shrink-0 place-items-center rounded-full bg-[#0B1220] font-archivo text-[9px] font-bold text-white">C</div>}
        <div>
          <div className={`whitespace-pre-wrap break-words rounded-[13px] px-3.5 py-2.5 text-[13px] leading-relaxed ${
            mine ? "rounded-br-[4px] bg-[#0055FF] text-white" : "rounded-bl-[4px] border border-[#E2E8F0] bg-white text-slate-800"}`}>
            {msg.body}
            <ChatAttachment att={msg.attachment} mine={mine} />
          </div>
          <div className={`mt-1 text-[10.5px] text-slate-400 ${mine ? "text-right" : ""}`}>{fmtTime(msg.created_at)}{VIA[msg.via] ? ` · ${VIA[msg.via]}` : ""}</div>
        </div>
      </div>
    </div>
  );
}

const VIA = { email: "by email", sms: "by text" };

function ConnieReply({ msg }) {
  return (
    <div className="flex justify-start" data-testid={`portal-chat-connie-${msg.id}`}>
      <div className="max-w-[84%] flex items-start gap-2">
        <ConnieMark size={24} orbit={false} testId={`portal-chat-connie-mark-${msg.id}`} />
        <div>
          <div className="mb-1 text-[9.5px] font-bold uppercase tracking-[0.16em] text-[#0044CC]">Connie · Connexis</div>
          <div className="connie-bubble break-words text-[13px]"><ConnieText text={msg.body} /></div>
          <div className="mt-1 text-[10px] text-slate-400">
            {msg.handed ? "A person on the desk will pick this up too · " : ""}{fmtTime(msg.created_at)}{VIA[msg.via] ? ` · sent ${VIA[msg.via]}` : ""}
          </div>
        </div>
      </div>
    </div>
  );
}
